(function () {
  'use strict';

  // Elemental magnesium % by weight for each supported magnesium compound.
  // Keys must match the <option value="..."> values rendered by
  // snippets/magnesium-calculator-type-select.liquid.
  var ELEMENTAL_PERCENTAGES = {
    glycinate: 0.12,
    citrate: 0.16,
    oxide: 0.60,
    hydroxide: 0.42,
    malate: 0.14,
    threonate: 0.08,
    taurate: 0.09,
    aquamin: 0.33
  };

  var selectors = {
    intro: '[data-mg-intro]',
    hasNumbersButton: '[data-mg-has-numbers]',
    noNumbersButton: '[data-mg-no-numbers]',
    noNumbersScreen: '[data-mg-no-numbers-screen]',
    noNumbersBack: '[data-mg-no-numbers-back]',
    form: '[data-mg-form]',
    formBack: '[data-mg-form-back]',
    rows: '[data-mg-rows]',
    row: '[data-mg-row]',
    rowTemplate: '[data-mg-row-template]',
    rowRemove: '[data-mg-row-remove]',
    addRow: '[data-mg-add-row]',
    type: '[data-mg-type]',
    amount: '[data-mg-amount]',
    claimed: '[data-mg-claimed]',
    result: '[data-mg-result]',
    resultBanner: '[data-mg-result-banner]',
    resultTitle: '[data-mg-result-title]',
    resultDetail: '[data-mg-result-detail]',
    scienceNote: '[data-mg-science-note]',
    resultRows: '[data-mg-result-rows]',
    resultRowTemplate: '[data-mg-result-row-template]',
    tallyActual: '[data-mg-tally-actual]',
    tallyClaimed: '[data-mg-tally-claimed]',
    tallyGapRow: '[data-mg-tally-gap-row]',
    tallyGapLabel: '[data-mg-tally-gap-label]',
    tallyGap: '[data-mg-tally-gap]',
    editButton: '[data-mg-edit]'
  };

  var classes = {
    hidden: 'is-hidden',
    match: 'magnesium-calculator__result-banner--match',
    buffered: 'magnesium-calculator__result-banner--buffered',
    overstated: 'magnesium-calculator__result-banner--overstated',
    gapBuffered: 'magnesium-calculator__tally-row--gap-buffered',
    gapOverstated: 'magnesium-calculator__tally-row--gap-overstated'
  };

  function roundHalfUp(value) {
    return Math.floor(value + 0.5 + 1e-9);
  }

  function classifyResult(actual, claimed) {
    if (actual === claimed) return 'match';
    if (actual < claimed) return 'buffered';
    return 'overstated';
  }

  class MagnesiumCalculator extends HTMLElement {
    constructor() {
      super();
      this.onHasNumbersClick = this.onHasNumbersClick.bind(this);
      this.onNoNumbersClick = this.onNoNumbersClick.bind(this);
      this.onNoNumbersBackClick = this.onNoNumbersBackClick.bind(this);
      this.onFormBackClick = this.onFormBackClick.bind(this);
      this.onAddRowClick = this.onAddRowClick.bind(this);
      this.onRowsClick = this.onRowsClick.bind(this);
      this.onRowsChange = this.onRowsChange.bind(this);
      this.onFormSubmit = this.onFormSubmit.bind(this);
      this.onEditClick = this.onEditClick.bind(this);
    }

    connectedCallback() {
      this.intro = this.querySelector(selectors.intro);
      this.hasNumbersButton = this.querySelector(selectors.hasNumbersButton);
      this.noNumbersButton = this.querySelector(selectors.noNumbersButton);
      this.noNumbersScreen = this.querySelector(selectors.noNumbersScreen);
      this.noNumbersBack = this.querySelector(selectors.noNumbersBack);
      this.form = this.querySelector(selectors.form);
      this.formBack = this.querySelector(selectors.formBack);
      this.rowsContainer = this.querySelector(selectors.rows);
      this.rowTemplate = this.querySelector(selectors.rowTemplate);
      this.addRowButton = this.querySelector(selectors.addRow);
      this.claimedInput = this.querySelector(selectors.claimed);
      this.resultPanel = this.querySelector(selectors.result);
      this.resultBanner = this.querySelector(selectors.resultBanner);
      this.resultTitle = this.querySelector(selectors.resultTitle);
      this.resultDetail = this.querySelector(selectors.resultDetail);
      this.scienceNote = this.querySelector(selectors.scienceNote);
      this.resultRows = this.querySelector(selectors.resultRows);
      this.resultRowTemplate = this.querySelector(selectors.resultRowTemplate);
      this.tallyActual = this.querySelector(selectors.tallyActual);
      this.tallyClaimed = this.querySelector(selectors.tallyClaimed);
      this.tallyGapRow = this.querySelector(selectors.tallyGapRow);
      this.tallyGapLabel = this.querySelector(selectors.tallyGapLabel);
      this.tallyGap = this.querySelector(selectors.tallyGap);
      this.editButton = this.querySelector(selectors.editButton);

      this.hasNumbersButton.addEventListener('click', this.onHasNumbersClick);
      this.noNumbersButton.addEventListener('click', this.onNoNumbersClick);
      this.noNumbersBack.addEventListener('click', this.onNoNumbersBackClick);
      this.formBack.addEventListener('click', this.onFormBackClick);
      this.addRowButton.addEventListener('click', this.onAddRowClick);
      this.rowsContainer.addEventListener('click', this.onRowsClick);
      this.rowsContainer.addEventListener('change', this.onRowsChange);
      this.form.addEventListener('submit', this.onFormSubmit);
      this.editButton.addEventListener('click', this.onEditClick);

      this.updateRemoveButtonsVisibility();
    }

    disconnectedCallback() {
      this.hasNumbersButton.removeEventListener('click', this.onHasNumbersClick);
      this.noNumbersButton.removeEventListener('click', this.onNoNumbersClick);
      this.noNumbersBack.removeEventListener('click', this.onNoNumbersBackClick);
      this.formBack.removeEventListener('click', this.onFormBackClick);
      this.addRowButton.removeEventListener('click', this.onAddRowClick);
      this.rowsContainer.removeEventListener('click', this.onRowsClick);
      this.rowsContainer.removeEventListener('change', this.onRowsChange);
      this.form.removeEventListener('submit', this.onFormSubmit);
      this.editButton.removeEventListener('click', this.onEditClick);
    }

    onHasNumbersClick() {
      this.intro.classList.add(classes.hidden);
      this.form.classList.remove(classes.hidden);
    }

    onNoNumbersClick() {
      this.intro.classList.add(classes.hidden);
      this.noNumbersScreen.classList.remove(classes.hidden);
    }

    onNoNumbersBackClick() {
      this.noNumbersScreen.classList.add(classes.hidden);
      this.intro.classList.remove(classes.hidden);
    }

    onFormBackClick() {
      this.form.classList.add(classes.hidden);
      this.intro.classList.remove(classes.hidden);
    }

    onAddRowClick() {
      this.addRow();
    }

    onRowsChange(event) {
      if (!event.target.matches(selectors.type)) return;

      const row = event.target.closest(selectors.row);
      const isLastRow = row === this.rowsContainer.lastElementChild;

      if (isLastRow && event.target.value) this.addRow();
    }

    addRow() {
      const fragment = this.rowTemplate.content.cloneNode(true);
      this.rowsContainer.appendChild(fragment);
      this.updateRemoveButtonsVisibility();
    }

    onRowsClick(event) {
      const removeButton = event.target.closest(selectors.rowRemove);
      if (!removeButton) return;

      const row = removeButton.closest(selectors.row);
      if (row) row.remove();

      this.updateRemoveButtonsVisibility();
    }

    updateRemoveButtonsVisibility() {
      const rows = this.rowsContainer.querySelectorAll(selectors.row);

      rows.forEach((row) => {
        const removeButton = row.querySelector(selectors.rowRemove);
        if (removeButton) removeButton.classList.toggle(classes.hidden, rows.length === 1);
      });
    }

    onFormSubmit(event) {
      event.preventDefault();

      const rows = this.rowsContainer.querySelectorAll(selectors.row);
      let actualTotal = 0;
      let completeCount = 0;
      let hasPartialRow = false;
      const breakdown = [];

      rows.forEach((row) => {
        const typeSelect = row.querySelector(selectors.type);
        const amountInput = row.querySelector(selectors.amount);
        const amount = parseFloat(amountInput.value);
        const hasType = !!typeSelect.value;
        const hasAmount = !isNaN(amount) && amount > 0;

        typeSelect.setCustomValidity('');
        amountInput.setCustomValidity('');

        if (!hasType && !hasAmount) return; // fully empty row -- ignore

        if (hasType && hasAmount) {
          completeCount++;
          const percentage = ELEMENTAL_PERCENTAGES[typeSelect.value];
          const elementalMg = amount * percentage;
          actualTotal += elementalMg;

          breakdown.push({
            typeLabel: typeSelect.selectedOptions[0] ? typeSelect.selectedOptions[0].text : typeSelect.value,
            amount: amount,
            percentage: percentage,
            elementalMg: elementalMg
          });
          return;
        }

        // partially filled row -- can't be calculated, block submission
        hasPartialRow = true;
        const message = this.dataset.rowIncompleteMessage;
        if (!hasType) typeSelect.setCustomValidity(message);
        if (!hasAmount) amountInput.setCustomValidity(message);
      });

      // Only nudge for "at least one row" when every row is fully empty --
      // a partial row already carries its own, more specific message.
      if (completeCount === 0 && !hasPartialRow) {
        rows[0].querySelector(selectors.type).setCustomValidity(this.dataset.atLeastOneMessage);
      }

      if (!this.form.reportValidity()) return;

      // Round before comparing to avoid floating point summation artifacts
      // (e.g. 0.1 + 0.2 style errors) -- inputs are whole mg, so this is not
      // a deliberate accuracy tolerance, just floating point safety.
      const actualRounded = roundHalfUp(actualTotal);
      const claimed = roundHalfUp(parseFloat(this.claimedInput.value));

      this.renderResult(actualRounded, claimed, breakdown);
    }

    renderResult(actual, claimed, breakdown) {
      const state = classifyResult(actual, claimed);
      const gap = Math.abs(claimed - actual);

      this.resultBanner.classList.toggle(classes.match, state === 'match');
      this.resultBanner.classList.toggle(classes.buffered, state === 'buffered');
      this.resultBanner.classList.toggle(classes.overstated, state === 'overstated');

      const titleKey = { match: 'matchTitle', buffered: 'bufferedTitle', overstated: 'overstatedTitle' }[state];
      const detailKey = { match: 'matchDetail', buffered: 'bufferedDetail', overstated: 'overstatedDetail' }[state];

      this.resultTitle.textContent = this.dataset[titleKey] || '';
      this.resultDetail.textContent = (this.dataset[detailKey] || '')
        .replace('[[actual]]', actual)
        .replace('[[claimed]]', claimed)
        .replace('[[gap]]', gap);

      if (this.scienceNote) {
        this.scienceNote.textContent = state === 'buffered' ? (this.dataset.scienceNote || '') : '';
        this.scienceNote.classList.toggle(classes.hidden, state !== 'buffered');
      }

      this.renderBreakdownTable(breakdown);
      this.renderTally(actual, claimed, gap, state);

      this.form.classList.add(classes.hidden);
      this.resultPanel.classList.remove(classes.hidden);
      this.resultPanel.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    renderBreakdownTable(breakdown) {
      this.resultRows.innerHTML = '';

      breakdown.forEach((row) => {
        const fragment = this.resultRowTemplate.content.cloneNode(true);
        const detail = (this.dataset.breakdownRowDetail || '')
          .replace('[[percentage]]', Math.round(row.percentage * 100) + '%')
          .replace('[[elemental]]', row.elementalMg.toFixed(1));

        fragment.querySelector('[data-mg-cell-type]').textContent = row.typeLabel;
        fragment.querySelector('[data-mg-cell-amount]').textContent = row.amount + 'mg';
        fragment.querySelector('[data-mg-cell-detail]').textContent = detail;
        this.resultRows.appendChild(fragment);
      });
    }

    renderTally(actual, claimed, gap, state) {
      this.tallyActual.textContent = actual + 'mg';
      this.tallyClaimed.textContent = claimed + 'mg';
      this.tallyGap.textContent = gap + 'mg';

      this.tallyGapRow.classList.toggle(classes.hidden, state === 'match');
      this.tallyGapRow.classList.toggle(classes.gapBuffered, state === 'buffered');
      this.tallyGapRow.classList.toggle(classes.gapOverstated, state === 'overstated');

      if (this.tallyGapLabel) {
        this.tallyGapLabel.textContent = state === 'overstated'
          ? this.dataset.tallyGapLabelOverstated || ''
          : this.dataset.tallyGapLabelBuffered || '';
      }
    }

    onEditClick() {
      this.resultPanel.classList.add(classes.hidden);
      this.form.classList.remove(classes.hidden);
    }
  }

  customElements.get('magnesium-calculator') || customElements.define('magnesium-calculator', MagnesiumCalculator);
})();
